import React from 'react';
import { differenceInMonths } from 'date-fns';
import { useFormikContext } from 'formik';
import { lowerCase, upperFirst } from 'lodash';

import { Button } from 'shared/components/display';

import axiosClient from 'shared/axiosClient';
import { CREATOR_COPY } from 'shared/config/copy';
import { API_USER_ROUTES } from 'shared/config/routes/api/apiUserRoutes';
import { useToasts } from 'shared/hooks/useToasts';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import { BrkfstUser } from 'shared/typings/user';
import { UserStatus, UserType } from 'shared/typings/user/enums';
import { formatISODate } from 'shared/utilities/dateUtility';
import { downloadFile } from 'shared/utilities/fileUtility';
import { LinkCreator } from 'shared/utilities/linkUtility';
import { CHILD_TAGS, TEENAGER_TAGS } from 'shared/utilities/tagUtility';

import { SearchBarState } from '../../molecules/CreatorSearch';
import { formatSearchQuery } from '../../molecules/CreatorSearch/utils';

interface Props {
  className?: string;
  initialValues: SearchBarState;
}

const CreatorsCSVFormButton: React.FC<Props> = ({ className, initialValues }) => {
  const { values } = useFormikContext<SearchBarState>();
  const { setErrorToast } = useToasts();

  const generateCreatorSearchResultsCSV = (results: BrkfstUser[]) => {
    const creatorInformation = results.map((item) => {
      const { country, state, addressLine1, addressLine2, city, postalCode } = item.mailingAddress ?? {};
      return {
        'creator id': item.id,
        'registration date': item.dateCreated ? formatISODate(new Date(item.dateCreated)) : '',
        'last login date': item.dateUpdated ? formatISODate(new Date(item.dateUpdated)) : '',
        'creator first name': item.firstName,
        'creator last name': item.lastName,
        age: item.birthdate ? Math.floor(differenceInMonths(new Date(), new Date(item.birthdate)) / 12) : '',
        email: item.email,
        gender: item.gender || '',
        'account verification status': UserStatus[item.status],
        'vetting status': item.vettingStatus,
        onboarded: item.onboarded,
        'stripe account link': item.stripeAccountId
          ? `https://dashboard.stripe.com/connect/accounts/${item.stripeAccountId}`
          : '',
        summary: item.summary || '',
        'instagram link': item.socialMediaInfo?.instagram
          ? `https://www.instagram.com/${item.socialMediaInfo?.instagram}`
          : '',
        'portfolio link': item.portfolioLink,
        'tiktok link': item.socialMediaInfo?.tiktok ? `https://www.tiktok.com/@${item.socialMediaInfo.tiktok}` : '',
        tags: item.creatorTags
          ?.filter(({ category }) => !['age', 'model'].includes(category))
          .map(({ value }) => value),
        'full address': `${addressLine1} ${addressLine2 || ''} ${city || ''} ${state?.name || ''} ${
          country?.name || ''
        } ${postalCode || ''}`,
        city: city || '',
        state: state?.name || '',
        country: country?.name || '',
        note: item.note || '',
        'profile link':
          window.location.origin +
          LinkCreator.createLink({
            userType: UserType.ORG,
            routeKey: 'USER_PROFILE',
            variables: {
              userId: item.id,
            },
          }),
        'referral source': item.referrer ? `${item.referralSource} - ${item.referrer} ` : item.referralSource || '',
        models: [
          ...item.fauxTags?.filter(({ category }) => category === 'model').map(({ value }) => value),
          ...item.creatorTags
            .filter(({ category, value }) => category === 'model' && ![...CHILD_TAGS, ...TEENAGER_TAGS].includes(value))
            .map(({ value }) => value),
        ],
      };
    });
    const refinedData: Array<any> = [];
    const titleColumns = Object.keys(creatorInformation[0]).map((title) => upperFirst(lowerCase(title)));
    let csvContent = '';

    refinedData.push(titleColumns);
    creatorInformation.forEach((item) => {
      refinedData.push(Object.values(item));
    });

    const escapeQuotes = (value) => `"${String(value).split('"').join('""')}"`;
    refinedData.forEach((row) => {
      csvContent += row.map(escapeQuotes).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8,' });
    const objUrl = URL.createObjectURL(blob);
    downloadFile({ url: objUrl, name: `Creators_${formatISODate(new Date())}.csv` });
  };

  const handleDownloadCSV = () => {
    const searchQuery = formatSearchQuery(values, initialValues);
    axiosClient
      .httpRequest<{ data: BrkfstUser[]; total: number }>({
        url: LinkCreator.createApiLink({
          route: API_USER_ROUTES.FIND_CREATORS,
        }),
        method: 'GET',
        params: { ...searchQuery, downloadAll: true }, // setting downloadAll to true will return all matching creators, unpaginated
      })
      .then((response) => {
        generateCreatorSearchResultsCSV(response.data.data);
      })
      .catch((e) => {
        setErrorToast({
          message: CREATOR_COPY.ERROR_CSV_DOWNLOAD_FAILED,
        });
      });
  };

  return (
    <Button variant={BUTTON_VARIANTS.OPTION} className={className} onClick={handleDownloadCSV}>
      Export CSV File
    </Button>
  );
};

export default CreatorsCSVFormButton;
